import { withErrorBoundary } from '@sentry/react';
import React from 'react';
import { Redirect, useHistory, useRouteMatch } from 'react-router-dom';
import { useQuery, useSubscription } from 'urql';
import { assets } from '../../assets';
import Button from '../../components/buttons/button';
import { BlurredContainerM2030 } from '../../components/containers/containers';
import DefaultScreen from '../../components/containers/default-screen';
import DataRow from '../../components/data-viewers/data-row/data-row';
import { Fallback } from '../../components/utils/error-boundary';
import { formatDateTime } from '../../helpers/date-formatters';
import { formatNumber } from '../../helpers/number-formatters';
import { StatusIcon, stringifyStatus } from '../../helpers/type-formatters';
import { GraphqlOperations } from '../../logic/graphql/operations';
import {
  MoneyTransferListItem,
  Query,
  Status,
  Subscription,
} from '../../logic/graphql/types/types.d';
import { Paths } from '../../navigation/paths';
import { FormattedMessage, useIntl } from 'react-intl';

const MoneyTransferDetails = (): JSX.Element => {
  const history = useHistory();
  const match = useRouteMatch();
  const intl = useIntl();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const id: string = (match.params as any).id || '';

  const [moneyTransferQueryResult, reexecuteMoneyTransferQuery] = useQuery<Query>({
    query: GraphqlOperations.queries.get_money_transfer.queryString,
    variables: GraphqlOperations.queries.get_money_transfer.createVars(id),
    pause: id === '',
    requestPolicy: 'network-only',
  });
  const moneyTransfer = moneyTransferQueryResult.data?.get_money_transfer;

  const handleUpdateMoneyTransferSubscription = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    subscriptionMoneyTransfers: MoneyTransferListItem[] = [],
    response: Subscription,
  ): MoneyTransferListItem[] => {
    if (response && response.update_money_transfer) {
      if (
        response.update_money_transfer.transaction_code ===
        moneyTransferQueryResult.data?.get_money_transfer.transaction_code
      ) {
        reexecuteMoneyTransferQuery();
      }
    }
    return [];
  };

  useSubscription(
    {
      query: GraphqlOperations.subscriptions.update_money_transfer.subscriptionString,
    },
    handleUpdateMoneyTransferSubscription,
  );

  const handleBackClick = (): void => {
    history.goBack();
  };

  const handleCancelClick = (): void => {
    history.push(Paths.contact);
  };

  if (id === '') {
    return <Redirect to={Paths.money_transfers} />;
  }

  const title = moneyTransfer?.currency.currency
    ? moneyTransfer.currency.currency +
      intl.formatMessage({ id: 'moneyTransfers.details.titleTransferExtension' })
    : intl.formatMessage({ id: 'moneyTransfers.details.defaultTitle' });
  const titleWithTransactionCode = moneyTransfer?.transaction_code
    ? title + ' - ' + moneyTransfer.transaction_code
    : title;

  return (
    <DefaultScreen
      background={assets.images.money_transfers_bg}
      title={titleWithTransactionCode}
      onBackClick={handleBackClick}
      loading={moneyTransferQueryResult.fetching}
      rightContent={
        <aside className="flex flex-row items-center">
          <StatusIcon status={moneyTransfer?.status} />
          <h2 className="ml-2">{stringifyStatus(moneyTransfer?.status, intl)}</h2>
        </aside>
      }
    >
      <div className="grid row-gap-1 col-gap-3  grid-cols-1 lg:grid-cols-2 mb-6">
        <BlurredContainerM2030>
          <h2 className="mb-6">
            <FormattedMessage id="moneyTransfers.details.dataLabel" />
          </h2>
          <DataRow
            label={intl.formatMessage({ id: 'moneyTransfers.details.fromAccountLabel' })}
            value={moneyTransfer?.from_account}
          />
          <DataRow
            label={intl.formatMessage({ id: 'moneyTransfers.details.toAccountLabel' })}
            value={moneyTransfer?.to_account}
          />
          <DataRow
            label={intl.formatMessage({ id: 'moneyTransfers.details.currencyLabel' })}
            value={moneyTransfer?.currency.currency}
          />
          <DataRow
            label={intl.formatMessage({ id: 'moneyTransfers.details.amountLabel' })}
            value={formatNumber(moneyTransfer?.amount, moneyTransfer?.currency.currency)}
          />
          <DataRow
            label={intl.formatMessage({ id: 'moneyTransfers.details.transferDateLabel' })}
            value={formatDateTime(moneyTransfer?.order_date)}
          />
          <DataRow
            label={intl.formatMessage({ id: 'moneyTransfers.details.fulfillmentDateLabel' })}
            value={formatDateTime(moneyTransfer?.fulfillment_date)}
          />
        </BlurredContainerM2030>
      </div>
      {moneyTransfer?.status === Status.Active ? (
        <Button secondary onClick={handleCancelClick}>
          <FormattedMessage id="moneyTransfers.details.cancelButton" />
        </Button>
      ) : null}
    </DefaultScreen>
  );
};

export default withErrorBoundary(MoneyTransferDetails, { fallback: Fallback });
